import ApiService from '@core/api/services/ApiService';
import UrlResolver from '@core/url-resolver/UrlResolver';
import { DebouncedFunc } from 'lodash';
import debounce from 'lodash/debounce';
import { useRef, useState } from 'react';

interface useProductSearchResult {
  doSearch: DebouncedFunc<(searchTerm: string) => Promise<void>>;
  goToResults: (searchTerm: string, filters?: Record<string, string | undefined>) => void;
  results: ProductViewModel[];
}

const useProductSearch = (maxResults: number, cultureCode: string, delay = 275): useProductSearchResult => {
  const [results, setResults] = useState<ProductViewModel[]>([]);

  const goToResults = (searchTerm: string, filters?: Record<string, string | undefined>): void => {
    let url = `/sitefiles/handlers/searchRedirect.ashx/?searchTerm=${searchTerm}&culture=${cultureCode}`;

    if (filters) {
      for (const key of Object.keys(filters)) {
        const value = filters[key];

        if (value) {
          url += `&${key}=${value}`;
        }
      }
    }

    window.location.href = window.location.href = url;
  };

  const doSearchInternal = async (searchTerm: string): Promise<void> => {
    if (searchTerm.length <= 2) {
      setResults([]);
      return;
    }

    const params: ProductSearchRequest = {
      searchTerm,
      cultureCode,
      maxResults,
      excludeLinkedProducts: true,
    };

    try {
      const result = await ApiService.request<ProductSearchResponse>({
        baseUrl: UrlResolver.getDomain(),
        controller: 'typeahead',
        slug: 'search',
        method: 'GET',
        params,
      });

      setResults(result.products || []);
    } catch {
      setResults([]);
    }
  };

  const debounceRef = useRef(debounce(doSearchInternal, delay));

  return {
    doSearch: debounceRef.current,
    goToResults,
    results,
  };
};

export default useProductSearch;
